import React, { useState } from 'react';
import { Building2, Target, Zap, MessageSquare, User } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import { organizationLoginRequest, personalLoginRequest } from '../config/authConfig';

interface LoginPageProps {
  navigateTo: (page: string) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ navigateTo }) => {
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle Microsoft Entra login
   * Always uses Popup flow
   */
  const handleEntraLogin = async (
    request: typeof organizationLoginRequest | typeof personalLoginRequest
  ) => {
    setError(null);
    setLoading(true);

    try {
      console.log('Using loginPopup for authentication');
      const response = await instance.loginPopup(request);
      if (response.account) {
        instance.setActiveAccount(response.account);
      }
      setLoading(false);
      // User is now authenticated, parent component will detect and re-render
    } catch (error: any) {
      console.error('Entra login error:', error);
      setError(error.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-gray-50 p-3 sm:p-4 md:p-6 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl border border-gray-100 p-4 sm:p-6 md:p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-light text-blue-600 mb-4 sm:mb-6">
            Welcome to Advisio
          </h1>
          <p className="text-gray-600 text-sm sm:text-base mb-6">
            Professional slides, in minutes.
          </p>
          <ul className="text-left text-gray-600 text-sm space-y-3">
            <li className="flex items-center">
              <Target className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
              Build a clear storyline and key messages
            </li>
            <li className="flex items-center">
              <Zap className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
              Generate slide titles and draft content fast
            </li>
            <li className="flex items-center">
              <MessageSquare className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
              Iterate with chat and regenerate only what changes
            </li>
          </ul>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6">
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
              {error}
            </div>
          </div>
        )}

        {/* Sign In Button */}
        <div className="mb-6">
          <button
            type="button"
            onClick={() => handleEntraLogin(organizationLoginRequest)}
            disabled={loading}
            className="w-full flex justify-center items-center py-2.5 sm:py-3 px-3 sm:px-4 border border-transparent rounded-md shadow-sm text-xs sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Opening sign in...
              </>
            ) : (
              <>
                <Building2 className="w-5 h-5 mr-2" />
                Sign in with work account
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => handleEntraLogin(personalLoginRequest)}
            disabled={loading}
            className="mt-3 w-full flex justify-center items-center py-2.5 sm:py-3 px-3 sm:px-4 border border-gray-300 rounded-md shadow-sm text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <User className="w-5 h-5 mr-2" />
            Sign in with personal account
          </button>
        </div>

        {/* Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800 text-center">
            <strong>New user?</strong> Click "Sign in" and select "Create one" on the login page to create your account.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
